import type {AgentSelection, SessionState} from '@microsoft/agent-host-protocol'
import type {EstablishedAhpSessionView} from './contracts'

const CUSTOM_AGENT_RESOLUTION_TIMEOUT_MS = 15_000
type Customization = NonNullable<SessionState['customizations']>[number]
type ContainerCustomization = Extract<Customization, {children?: unknown}>
type AgentCustomization = Extract<NonNullable<ContainerCustomization['children']>[number], {type: 'agent'}>

export interface CustomAgentSelectionRequest {
  readonly slug: string
  readonly displayName?: string
}

function selectableAgents(customizations: readonly Customization[] | undefined): AgentCustomization[] {
  if (!customizations) return []

  return customizations.flatMap(customization => {
    if (!('children' in customization) || !customization.enabled) return []
    return (customization.children ?? []).filter(
      (child): child is AgentCustomization =>
        child.type === 'agent' && child.enabled !== false && child.disableUserInvocation !== true,
    )
  })
}

function advertisedAgents(customizations: readonly Customization[] | undefined): AgentCustomization[] {
  if (!customizations) return []
  return customizations.flatMap(customization =>
    'children' in customization
      ? (customization.children ?? []).filter((child): child is AgentCustomization => child.type === 'agent')
      : [],
  )
}

function agentNameFromUri(uri: string): string | undefined {
  const prefix = 'copilot-agent:/'
  if (!uri.startsWith(prefix)) return undefined
  const encodedName = uri.slice(prefix.length)
  if (!encodedName || encodedName.includes('/') || encodedName.includes('?') || encodedName.includes('#')) {
    return undefined
  }

  try {
    return decodeURIComponent(encodedName)
  } catch {
    return undefined
  }
}

export function findCustomAgentSelection(
  state: SessionState | undefined,
  request: CustomAgentSelectionRequest,
): AgentSelection | undefined {
  const agents = selectableAgents(state?.customizations)
  const slugMatch = agents.find(agent => agentNameFromUri(agent.uri) === request.slug)
  if (slugMatch) return {uri: slugMatch.uri}

  const expectedDisplayName = request.displayName || request.slug
  const displayNameMatches = agents.filter(agent => agent.name === expectedDisplayName)
  const [displayNameMatch] = displayNameMatches
  return displayNameMatches.length === 1 && displayNameMatch ? {uri: displayNameMatch.uri} : undefined
}

export async function resolveCustomAgentSelection(
  view: EstablishedAhpSessionView,
  request: CustomAgentSelectionRequest,
  timeoutMs = CUSTOM_AGENT_RESOLUTION_TIMEOUT_MS,
): Promise<AgentSelection> {
  const current = findCustomAgentSelection(view.getSnapshot().sessionState, request)
  if (current) return current

  return new Promise<AgentSelection>((resolve, reject) => {
    let unsubscribe = () => {}
    const timeout = setTimeout(() => {
      unsubscribe()
      const agents = advertisedAgents(view.getSnapshot().sessionState?.customizations)
      const requestedName = request.displayName || request.slug
      if (agents.length === 0) {
        reject(
          new Error(
            `The AHP host did not advertise any custom agents for this session, so "${requestedName}" could not be selected.`,
          ),
        )
        return
      }
      reject(new Error(`The custom agent "${requestedName}" did not match the agents advertised by the AHP host.`))
    }, timeoutMs)
    const check = () => {
      const selection = findCustomAgentSelection(view.getSnapshot().sessionState, request)
      if (!selection) return
      clearTimeout(timeout)
      unsubscribe()
      resolve(selection)
    }

    unsubscribe = view.subscribe(check)
    check()
  })
}
